import React, { useState, useRef, useEffect } from "react";
import api from "../utils/api";
import { detectDeviceOs } from "../utils/deviceOs";
import {
  FALLBACK_STRINGS,
  fetchChatStrings,
  quotaMessage,
  quotaRemainingText,
  suggestionsFor,
  formatMessageTime,
} from "../utils/chatLocale";
import {
  CHAT_SEEN_KEY,
  chatAlreadyOpened,
  markChatOpened,
} from "../utils/chatSession";
import "./ChatWidget.css";

// Channel id from the current URL (/channel/:id), for the assistant's context.
function currentChannelId() {
  const m = window.location.pathname.match(/\/channel\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

// Minimal markdown -> JSX: bold, inline code, bullet/numbered lists, paragraphs.
function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(p)) return <code key={i}>{p.slice(1, -1)}</code>;
    return p;
  });
}

function Markdown({ text }) {
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };

  (text || "").split("\n").forEach((raw) => {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet) {
      if (!list || list.type !== "ul") { flush(); list = { type: "ul", items: [] }; }
      list.items.push(bullet[1]);
    } else if (numbered) {
      if (!list || list.type !== "ol") { flush(); list = { type: "ol", items: [] }; }
      list.items.push(numbered[1]);
    } else if (line.trim() !== "") {
      flush();
      blocks.push({ type: "p", text: line });
    }
  });
  flush();

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === "ul") return <ul key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
        if (b.type === "ol") return <ol key={i}>{b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>;
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </>
  );
}

const SEEN_KEY = CHAT_SEEN_KEY;

// How long after arriving the assistant introduces itself. Long enough that the
// page has drawn and the user is not interrupted mid-glance, short enough that
// they are still looking at the screen.
const AUTO_OPEN_DELAY_MS = 4000;

// Module-level so the effect below can use it without closing over a value that
// changes identity on every render.
function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  } catch {
    return {};
  }
}

// Open to every signed-in user.
export default function ChatWidget() {
  // The assistant's wording comes from the backend on first open; until then
  // the plain English fallback keeps the widget renderable.
  const [t, setT] = useState(FALLBACK_STRINGS);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [quota, setQuota] = useState(null);
  const [startersOpen, setStartersOpen] = useState(false);

  // Clearing cannot be undone, so it takes two taps. The armed state lapses on
  // its own; an armed bin sitting there forever is its own trap.
  const [confirmClear, setConfirmClear] = useState(false);

  // A quiet nudge that there is something worth opening, shown once per session
  // so a returning user isn't badgered on every page they visit.
  const [hasNews, setHasNews] = useState(() => {
    try {
      return sessionStorage.getItem(SEEN_KEY) !== "1";
    } catch {
      return false;
    }
  });

  // The morning digest outranks the session nudge: an unread report is a real
  // reason for the dot, and it stays lit across pages until the conversation is
  // opened, not just until the first page is left.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/chat/unread")
      .then((res) => {
        if (cancelled || !res.data?.unread) return;
        setHasNews(true);
        try {
          sessionStorage.removeItem(SEEN_KEY);
        } catch {
          /* the dot still shows for this render */
        }
      })
      .catch(() => {
        // An older reader has no such endpoint; the session nudge stands.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!confirmClear) return undefined;
    const timer = setTimeout(() => setConfirmClear(false), 4000);
    return () => clearTimeout(timer);
  }, [confirmClear]);

  const bottomRef = useRef(null);
  const historyLoaded = useRef(false);

  // The newest line is the one worth seeing: the day's figures arrive at the
  // bottom of the conversation like any other message. Held off until the
  // history is in so the pane doesn't scroll an empty box.
  const followConversation = useRef(false);

  useEffect(() => {
    if (!followConversation.current) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, open]);

  // The assistant opens itself once a visit, so people discover it instead of
  // walking past the bubble. Never twice: the flag is set the moment the pane
  // opens, however it was opened, and is re-read when the timer fires in case
  // the user got there first.
  useEffect(() => {
    if (chatAlreadyOpened()) return undefined;

    const timer = setTimeout(() => {
      if (chatAlreadyOpened()) return;
      setOpen(true);
    }, AUTO_OPEN_DELAY_MS);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (open) markChatOpened();
  }, [open]);

  // Fetched on first open rather than on mount, so the widget costs nothing
  // for the users who never touch it.
  useEffect(() => {
    if (!open || historyLoaded.current) return;
    historyLoaded.current = true;

    let cancelled = false;
    setLoadingHistory(true);

    // The wording and the conversation land together. Drawing the greeting
    // before the text arrives would show it in English and then swap it.
    Promise.all([
      fetchChatStrings(readStoredUser()),
      api
        .get("/chat/history")
        .then((res) => res.data?.messages)
        .catch(() => {
          // A failed load just means an empty pane; the next answer still saves.
          historyLoaded.current = false;
          return null;
        }),
    ])
      .then(([strings, history]) => {
        if (cancelled) return;
        setT(strings);
        if (Array.isArray(history) && history.length > 0) {
          followConversation.current = true;
          setMessages(history);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const clearChat = async () => {
    const previous = messages;
    setStartersOpen(false);
    // Back to the greeting, so back to the top with it.
    followConversation.current = false;
    setMessages([]);
    try {
      await api.delete("/chat/history");
    } catch {
      setMessages(previous);
    }
  };

  // Pressing the same thumb again clears the rating — the second press reads as
  // "undo", never as a second vote. Applied locally first so the button responds
  // at once, and rolled back if the server refuses it.
  const rateMessage = async (id, rating) => {
    if (!id) return;
    const previous = messages.find((m) => m.id === id)?.rating || "";
    const next = previous === rating ? "" : rating;
    const apply = (value) =>
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, rating: value } : m))
      );

    apply(next);
    try {
      await api.post("/chat/feedback", { messageId: id, rating: next });
    } catch {
      apply(previous);
    }
  };

  const submitMessage = async (raw) => {
    const text = raw.trim();
    if (!text || sending) return;
    setStartersOpen(false);
    followConversation.current = true;
    // Stamped locally so the time shows at once; the server keeps its own, which
    // is what comes back on the next load.
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text, at: new Date().toISOString() },
    ]);
    setInput("");
    setSending(true);
    try {
      // History lives on the server; only the new message goes up.
      const res = await api.post("/chat", {
        message: text,
        context: {
          channelId: currentChannelId(),
          view: sessionStorage.getItem("adm_current_view") || "",
          language: t.languageName,
          platform: "web",
          deviceOs: detectDeviceOs(),
        },
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: res.data.reply,
          id: res.data.messageId,
          at: new Date().toISOString(),
        },
      ]);
      if (res.data.quota) setQuota(res.data.quota);
    } catch (err) {
      const q = err.response?.data?.quota;
      if (q) setQuota(q);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: quotaMessage(t, q, err.response?.data?.error || t.genericError),
          at: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendChat = (e) => {
    e.preventDefault();
    submitMessage(input);
  };

  const showQuota =
    quota && quota.limit > 0 && quota.remaining >= 0 && quota.remaining <= quota.limit / 2;
  const quotaExhausted = quota && quota.limit > 0 && quota.remaining === 0;

  // Guidance shouldn't stop after the first answer: each starter carries its own
  // follow-ups, so clicking one leads somewhere related rather than dead-ending.
  const suggestions = suggestionsFor(t, messages);

  // Not the same as an empty pane: the day's figures can be sitting there
  // without the user having said a word yet.
  const hasAsked = messages.some((m) => m.role === "user");

  const showFollowUps =
    !loadingHistory &&
    !sending &&
    !quotaExhausted &&
    hasAsked &&
    messages[messages.length - 1].role === "assistant" &&
    suggestions.length > 0;

  return (
    <>
      {open && (
        <div className="chat-popup">
          <div className="chat-header">
            <span className="chat-header-title">
              <i className="fa-solid fa-tv"></i> {t.title}
            </span>
            {/* Top right is where a person reaches to close a panel. It used
                to hold the bin, so people deleted their conversation while
                trying to put the assistant away. */}
            <button
              className="chat-close"
              type="button"
              onClick={() => setOpen(false)}
              title="Close"
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <div className="chat-body">
            {loadingHistory && (
              <div className="chat-loading-history">
                <i className="fa-solid fa-spinner fa-spin"></i>
              </div>
            )}
            {/* The introduction and the starters stay until the user actually
                asks something. The day's figures arrive on their own, so a first
                visit is not an empty pane and would otherwise skip both. */}
            {!loadingHistory && !hasAsked && (
              <div className="chat-msg chat-msg--assistant chat-greeting">
                {t.greeting}
              </div>
            )}
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <div className={`chat-msg chat-msg--${m.role}`}>
                  {m.role === "assistant" ? <Markdown text={m.content} /> : m.content}
                  {m.at && <span className="chat-time">{formatMessageTime(t, m.at)}</span>}
                </div>
                {/* Only answers the server actually stored can be rated; a local
                    error notice has no id and nothing to attach feedback to. */}
                {m.role === "assistant" && m.id && (
                  <div className="chat-rate">
                    <button
                      type="button"
                      className={`chat-rate-btn ${m.rating === "up" ? "is-up" : ""}`}
                      onClick={() => rateMessage(m.id, "up")}
                      title={t.rateUp}
                      aria-label={t.rateUp}
                      aria-pressed={m.rating === "up"}
                    >
                      <i className={`${m.rating === "up" ? "fa-solid" : "fa-regular"} fa-thumbs-up`}></i>
                    </button>
                    <button
                      type="button"
                      className={`chat-rate-btn ${m.rating === "down" ? "is-down" : ""}`}
                      onClick={() => rateMessage(m.id, "down")}
                      title={t.rateDown}
                      aria-label={t.rateDown}
                      aria-pressed={m.rating === "down"}
                    >
                      <i className={`${m.rating === "down" ? "fa-solid" : "fa-regular"} fa-thumbs-down`}></i>
                    </button>
                    {m.rating && <span className="chat-rate-thanks">{t.rateThanks}</span>}
                  </div>
                )}
              </React.Fragment>
            ))}
            {/* Below the day's figures rather than above them: the starters are
                what to do next, so they sit where the follow-ups will. */}
            {!loadingHistory && !hasAsked && (
              <>
                <div className="chat-empty">{t.empty}</div>
                <div className="chat-suggestions">
                  {suggestions.map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="chat-suggestion"
                      onClick={() => submitMessage(q)}
                      disabled={sending || quotaExhausted}
                    >
                      <span>{q}</span>
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  ))}
                </div>
              </>
            )}
            {sending && (
              <div className="chat-msg chat-msg--assistant chat-typing">
                <i className="fa-solid fa-spinner fa-spin"></i>
              </div>
            )}
            {showFollowUps && (
              <div className="chat-followups">
                {suggestions.map((q) => (
                  <button
                    key={q}
                    type="button"
                    className="chat-followup"
                    onClick={() => submitMessage(q)}
                  >
                    <span>{q}</span>
                    <i className="fa-solid fa-arrow-right"></i>
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          {showQuota && (
            <div className={`chat-quota ${quotaExhausted ? "chat-quota--empty" : ""}`}>
              {quotaExhausted ? t.quotaExhausted : quotaRemainingText(t, quota.remaining)}
            </div>
          )}

          {startersOpen && hasAsked && (
            <div className="chat-starter-menu">
              {t.starters.map((item) => (
                <button
                  key={item.q}
                  type="button"
                  className="chat-suggestion"
                  onClick={() => submitMessage(item.q)}
                  disabled={sending || quotaExhausted}
                >
                  <span>{item.q}</span>
                  <i className="fa-solid fa-arrow-right"></i>
                </button>
              ))}
            </div>
          )}

          <form className="chat-input-row" onSubmit={sendChat}>
            <input
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={quotaExhausted ? t.placeholderExhausted : t.placeholder}
              disabled={sending || quotaExhausted}
            />
            <button
              className="chat-send"
              type="submit"
              disabled={sending || quotaExhausted || !input.trim()}
            >
              <i className="fa-solid fa-paper-plane"></i>
            </button>
            {/* Only once the starter list above has gone; two copies of the same
                questions on screen is not a second chance to see them. */}
            {hasAsked && (
              <button
                type="button"
                className={`chat-starter-toggle ${startersOpen ? "is-open" : ""}`}
                onClick={() => setStartersOpen((o) => !o)}
                title={t.showStarters}
                aria-label={t.showStarters}
                aria-expanded={startersOpen}
                disabled={quotaExhausted}
              >
                <i className="fa-regular fa-lightbulb"></i>
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                className={`chat-clear ${confirmClear ? "is-confirming" : ""}`}
                onClick={() => {
                  if (!confirmClear) {
                    setConfirmClear(true);
                    return;
                  }
                  setConfirmClear(false);
                  clearChat();
                }}
                title={confirmClear ? t.clearConfirm : t.clear}
                aria-label={confirmClear ? t.clearConfirm : t.clear}
                disabled={sending}
              >
                <i className={`fa-solid ${confirmClear ? "fa-check" : "fa-trash-can"}`}></i>
              </button>
            )}
          </form>
        </div>
      )}

      <button
        className={`chat-launcher ${open ? "is-open" : ""} ${hasNews && !open ? "has-news" : ""}`}
        onClick={() => {
          setOpen((o) => !o);
          if (hasNews) {
            setHasNews(false);
            try {
              sessionStorage.setItem(SEEN_KEY, "1");
            } catch {
              /* private mode — the dot simply reappears next time */
            }
          }
        }}
        title={open ? "Close" : "mongrel.intelligence"}
        aria-label={open ? "Close" : "mongrel.intelligence"}
      >
        <i className={`fa-solid ${open ? "fa-xmark" : "fa-tv"}`}></i>
      </button>
    </>
  );
}
